import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Alert, AlertOptions, AlertType, ConfirmOptions, ConfirmRequest } from '@models/shared/utils/alert';

@Injectable({ providedIn: 'root' })
export class AlertService {
    private subject = new Subject<Alert>();
    private confirmSubject = new Subject<ConfirmRequest>();
    private defaultId = 'default-alert';
    flashErrors: string[] = [];

    // enable subscribing to alerts observable
    onAlert(id = this.defaultId): Observable<Alert> {
        return this.subject.asObservable().pipe(filter(x => x && x.id === id));
    }

    // Confirmation methods
    confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
        return new Promise(resolve => {
            this.confirmSubject.next({ message, options, resolve });
        });
    }

    onConfirm(): Observable<ConfirmRequest> {
        return this.confirmSubject.asObservable();
    }

    // convenience methods
    success(message: string | Array<string>, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.Success, message }));
    }

    error(message: string | Array<string> | { [key: string]: string[] }, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.Error, message: this.formatMessage(message) }));
    }

    // Flash error method
    flashError(message: string): void {
        this.flashErrors.push(message);
        setTimeout(() => {
            this.flashErrors = [];
        }, 3000);
    }

    // Format error messages
    private formatMessage(message: string | string[] | { [key: string]: string[] }): string {
        if (typeof message === 'string') {
            return message;
        } else if (Array.isArray(message)) {
            return message.join('<br>');
        } else if (typeof message === 'object') {
            return Object.values(message).flat().join('<br>');
        }
        return '';
    }

    info(message: string | Array<string>, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.Info, message }));
    }

    warn(message: string | Array<string>, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.Warning, message }));
    }

    // core alert method
    alert(alert: Alert) {
        alert.id = alert.id || this.defaultId;
        alert.autoClose = (alert.autoClose === undefined ? true : alert.autoClose);
        console.log(alert);
        this.subject.next(alert);
    }

    // clear alerts
    clear(id = this.defaultId) {
        this.subject.next(new Alert({ id }));
    }

    handleError(errorResponse: any): void {
        const error = errorResponse.error || errorResponse;

        if (error && !error.success) {
            const errorMessage = error.message || 'An unexpected error occurred.';
            const validationErrors = error.errors;

            if (validationErrors) {
                const formattedErrors = Object.keys(validationErrors).map(key =>
                    `${key}: ${validationErrors[key].join(', ')}`
                ).join(' ');

                this.error(`${errorMessage} - ${formattedErrors}`);
            } else {
                this.error(errorMessage);
            }
        } else {
            this.error('An unexpected error occurred.');
        }
    }
}
